import { IconBuildings, IconTag } from '@sequencehq/core-components'
import { fetchQuery } from '@sequencehq/api'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { MINUTES } from '@sequencehq/utils/dates'

type CustomerItem =
  DashboardApi20240730.GetCustomers.CustomersList['items'][number]

type CustomerAlias = {
  customerId: string
  value: string
}

const PAGE_SIZE = 100

const fetchCustomers = async (searchTerm: string) => {
  const data = await fetchQuery(
    apiQueryClient,
    dashboard20240730Client.getCustomers,
    {
      legalName: searchTerm || undefined,
      limit: PAGE_SIZE,
      sortBy: 'legalName' as const,
      sortOrder: 'ASC' as const
    },
    { staleTime: 1 * MINUTES }
  )
  return data?.items ?? []
}

const fetchCustomersPaginated = async (searchTerm: string, cursor?: string) => {
  const data = await fetchQuery(
    apiQueryClient,
    dashboard20240730Client.getCustomers,
    {
      legalName: searchTerm || undefined,
      limit: PAGE_SIZE,
      sortBy: 'legalName' as const,
      sortOrder: 'ASC' as const,
      after: cursor
    },
    { staleTime: 1 * MINUTES }
  )

  const items = data?.items ?? []
  const pagination = data?.pagination

  return {
    items,
    cursor: pagination?.after,
    hasMore: items.length === PAGE_SIZE && Boolean(pagination?.after)
  }
}

const fetchCustomersByIds = async (ids: string[]) => {
  const results = await Promise.all(
    ids.map(async id => {
      const response = await dashboard20240730Client.getCustomer({ id })
      return response.data
    })
  )
  return results.filter(
    (customer): customer is NonNullable<typeof customer> =>
      customer !== undefined && customer !== null
  )
}

/**
 * Standard customer filter config for MagicTable.
 * Maps customer selection to customer IDs.
 */
export const customerIdFilterConfig = {
  type: 'multiSelectAsync' as const,
  label: 'Customer',
  icon: IconBuildings,
  fetcher: fetchCustomers,
  paginatedFetcher: fetchCustomersPaginated,
  initializeOptions: fetchCustomersByIds,
  mapToOption: (customer: { legalName: string; id: string }) => ({
    label: customer.legalName,
    value: customer.id
  })
}

/**
 * Customer filter config that includes alias expansion.
 * Used in tables that filter by customer alias (e.g. usage events, seat events).
 * The `aliases` parameter should be the full list of customer aliases loaded via
 * `useLoadAllItemsQuery(dashboard20240730Client.getCustomerAliases, ...)`.
 */
export const aliasesByCustomerFilterConfig = (
  aliases: CustomerAlias[] | undefined
) => ({
  type: 'multiSelectAsync' as const,
  label: 'Customer',
  icon: IconBuildings,
  fetcher: async (searchTerm: string) => {
    const customers = await fetchCustomers(searchTerm)

    return customers.map(customer => ({
      customerName: customer.legalName,
      customerId: customer.id,
      aliases: (aliases ?? [])
        .filter(alias => alias.customerId === customer.id)
        .map(alias => alias.value)
    }))
  },
  paginatedFetcher: async (searchTerm: string, cursor?: string) => {
    const result = await fetchCustomersPaginated(searchTerm, cursor)

    return {
      items: result.items.map(customer => ({
        customerName: customer.legalName,
        customerId: customer.id,
        aliases: (aliases ?? [])
          .filter(alias => alias.customerId === customer.id)
          .map(alias => alias.value)
      })),
      cursor: result.cursor,
      hasMore: result.hasMore
    }
  },
  initializeOptions: async (values: string[]) => {
    const customerIds = values.map(value => value.split(',')[0])
    const customers = await fetchCustomersByIds(customerIds)

    return customers.map(customer => ({
      customerName: customer.legalName,
      customerId: customer.id,
      aliases: (aliases ?? [])
        .filter(alias => alias.customerId === customer.id)
        .map(alias => alias.value)
    }))
  },
  mapToOption: (customer: {
    customerName: string
    customerId: string
    aliases: string[]
  }) => {
    return {
      label: customer.customerName,
      value: [customer.customerId, ...customer.aliases].join(',')
    }
  }
})

const LABELS_PAGE_SIZE = 100

const fetchCustomerLabelsPaginated = async (
  searchTerm: string,
  cursor?: string
) => {
  const data = await fetchQuery(
    apiQueryClient,
    dashboardv99990101Client.getCustomersLabels,
    {
      search: searchTerm || undefined,
      limit: LABELS_PAGE_SIZE,
      after: cursor
    },
    { staleTime: 1 * MINUTES }
  )

  const items = data?.items ?? []
  const nextCursor = data?.pagination?.after

  return {
    // A customer may have an empty-string label; exclude it from the
    // selectable options. hasMore is derived from the raw page length (below)
    // so dropping the blank value never skews pagination.
    items: items.filter(label => label !== ''),
    cursor: nextCursor,
    // A full page plus a cursor means there may be more; guarding on the page
    // size avoids looping if the API returns a cursor on the last page.
    hasMore: items.length === LABELS_PAGE_SIZE && Boolean(nextCursor)
  }
}

/**
 * Customer label filter config for MagicTable. The search term drives the
 * `/customers/labels` endpoint server-side rather than filtering a
 * fully-loaded list. Single-select keeps the filter compatible with the
 * customers list endpoint's single-value `label` search. Values are label
 * strings, so they double as their own display label.
 */
export const customerLabelFilterConfig = {
  type: 'singleSelect' as const,
  paramName: 'label' as const,
  label: 'Label',
  icon: IconTag,
  fetcher: async (searchTerm: string) =>
    (await fetchCustomerLabelsPaginated(searchTerm)).items,
  paginatedFetcher: fetchCustomerLabelsPaginated,
  initializeOptions: async (values: string[]) => values,
  mapToOption: (label: string) => ({ value: label, label })
}
